import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Amplify } from '@aws-amplify/core';
import { RecoilRoot } from 'recoil';
import Page from 'components/layout/Page';
import DocumentView from 'components/DocumentView';
import amplifyConfig from 'amplifyConfig';

import StepRouter from 'navigation/StepRouter';
import ErrorBoundary from 'components/ErrorBoundary';
import ScenarioGetter from 'scenario/ScenarioGetter';
import Redirecter from 'Redirecter';
import { AuthEvent, validateState } from '@novozymes-digital/auth';
import Cookies from 'pages/Cookies';
import Privacy from 'pages/Privacy';
import TermsConditions from 'pages/TermsConditions';
import Legal from 'pages/Legal';
import { getUserType } from 'utils/getUser';
import Home from './pages/Home';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'authentication-wrapper': any;
    }
  }
}

Amplify.configure(amplifyConfig);

function App(): ReactElement {
  const [authLoaded, setAuthLoaded] = useState(false);
  const [authStateReturnURL, setAuthStateReturnURL] = useState('');
  const [userType, setUserType] = useState('not-logged-in');

  const onAuthEvent = useCallback(async (event) => {
    const authEvent = event as AuthEvent;
    if (authEvent.detail.newAuthState === 'signedIn') {
      setAuthLoaded(true);
      const currentUserType = await getUserType();
      setUserType(currentUserType);
    }
  }, []);
  // eslint-disable-next-line no-undef
  window.addEventListener('AuthStateChange', onAuthEvent);

  useEffect(() => {
    const goTOUrl = validateState();
    if (goTOUrl) {
      setAuthStateReturnURL(goTOUrl);
    }
  }, []);

  useEffect(() => {
    if (document.getElementById('gtm-init')) return;
    const script = document.createElement('script');
    script.id = 'gtm-init';
    script.innerHTML = `(function(w,d,s,l,g,i,h){w[l]=w[l]||[];w[l].push({gcm:1,
      server:h});w[g]=function(){w[l].push(arguments)};var c='denied';
      w[g]('consent','default',{ad_storage:c,ad_user_data:c,
      ad_personalization:c,analytics_storage:c,functionality_storage:c,
      personalization_storage:c,security_storage:'granted',
      wait_for_update:500});w[g]('set','ads_data_redaction',true);
      w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],j=d.createElement(s),
      dl=l!='dataLayer'?'&l='+l:'';j.async=true;
      j.src='https://'+(h??'www.googletagmanager.com')+'/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);})(window,document,'script',
      'dataLayer','gtag','GTM-KTWCZXC','gtm.novozymes.cloud');`;
    document.head.appendChild(script);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      script.parentNode?.removeChild(script);
    };
  }, []);

  // Cookie Information consent script
  useEffect(() => {
    if (document.getElementById('CookieConsent')) return;
    const cookieScript = document.createElement('script');
    cookieScript.id = 'CookieConsent';
    cookieScript.src = 'https://policy.app.cookieinformation.com/uc.js';
    cookieScript.setAttribute('data-culture', 'EN');
    cookieScript.setAttribute('data-gcm-version', '2.0');
    cookieScript.type = 'text/javascript';
    document.head.appendChild(cookieScript);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      cookieScript.parentNode?.removeChild(cookieScript);
    };
  }, []);

  return (
    <authentication-wrapper id="authentication" amplifyConfig={JSON.stringify(amplifyConfig)}>
      {authLoaded && authStateReturnURL ? (
        <RecoilRoot>
          <Router>
            <ErrorBoundary>
              <ScenarioGetter>
                <Redirecter redirectTo={authStateReturnURL}>
                  <Switch>
                    <Route path="/step/:stepUrl/:scenarioId?" component={StepRouter} />
                    <Route exact path="/cookies">
                      <Page hideProcessHeader hideFooter isHomePage>
                        <Cookies />
                      </Page>
                    </Route>
                    <Route exact path="/privacy">
                      <Page hideProcessHeader hideFooter isHomePage>
                        <Privacy />
                      </Page>
                    </Route>
                    <Route exact path="/terms">
                      <Page hideProcessHeader hideFooter isHomePage>
                        <TermsConditions />
                      </Page>
                    </Route>
                    <Route exact path="/legal">
                      <Page hideProcessHeader hideFooter isHomePage>
                        <Legal />
                      </Page>
                    </Route>

                    <Route exact path="/docs/:name">
                      <Page hideProcessHeader hideFooter isHomePage>
                        <DocumentView />
                      </Page>
                    </Route>
                    <Route path="/:scenarioId?">
                      <Page>
                        <Home />
                      </Page>
                    </Route>
                  </Switch>
                </Redirecter>
              </ScenarioGetter>
            </ErrorBoundary>
          </Router>
        </RecoilRoot>
      ) : null}
    </authentication-wrapper>
  );
}

export default App;
